import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Rocket, Newspaper, Car, CircleDot } from 'lucide-react';

const EducationSection = () => {
  const education = [
    {
      degree: 'Bachelor of Business Administration',
      specialization: 'Marketing & Sales',
      institution: 'ICCS, Pune University',
      period: '2013–2016',
      color: '#8B5CF6'
    },
    {
      degree: 'Higher Secondary',
      institution: 'Kendriya Vidyalaya, CBSE',
      period: '2010–2012',
      color: '#06B6D4'
    }
  ];

  const interests = [
    { icon: Rocket, label: 'Startups & Tech', color: '#8B5CF6' },
    { icon: Newspaper, label: 'Current Affairs', color: '#06B6D4' },
    { icon: Car, label: 'Roads & Rides', color: '#A78BFA' },
    { icon: CircleDot, label: 'Pool & Snooker', color: '#22D3EE' }
  ];

  return (
    <section className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2A1F5E]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-white/10 backdrop-blur-md border border-[#8B5CF6]/30 rounded-full text-[#8B5CF6] text-xs font-mono font-semibold tracking-wider">
                  EDUCATION
                </span>
              </div>
              <h2 className="text-4xl font-bold text-white flex items-center" data-testid="education-heading">
                <GraduationCap className="mr-3 text-[#8B5CF6]" size={36} style={{ filter: 'drop-shadow(0 0 10px #8B5CF6)' }} />
                Education
              </h2>
            </div>
            
            <div className="space-y-6">
              {education.map((edu, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, x: 5 }}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 relative overflow-hidden group"
                  data-testid={`education-item-${index}`}
                >
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-1 transition-all group-hover:w-2"
                    style={{ background: edu.color, boxShadow: `0 0 20px ${edu.color}60` }}
                  />
                  
                  <div className="ml-4">
                    <h3 className="text-lg font-bold text-white mb-1">{edu.degree}</h3>
                    {edu.specialization && (
                      <p className="text-sm mb-2" style={{ color: edu.color }}>{edu.specialization}</p>
                    )}
                    <p className="text-sm text-white/70 mb-2">{edu.institution}</p>
                    <p className="text-xs text-white/50 font-mono">{edu.period}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-white/10 backdrop-blur-md border border-[#06B6D4]/30 rounded-full text-[#06B6D4] text-xs font-mono font-semibold tracking-wider">
                  INTERESTS
                </span>
              </div>
              <h2 className="text-4xl font-bold text-white" data-testid="interests-heading">
                Interests
              </h2>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {interests.map((interest, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1, y: -5 }}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 text-center transition-all relative overflow-hidden group"
                  data-testid={`interest-item-${index}`}
                >
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity"
                    style={{ background: `radial-gradient(circle at center, ${interest.color} 0%, transparent 70%)` }}
                  />
                  
                  <div className="relative z-10">
                    <div 
                      className="inline-flex p-3 rounded-xl bg-white/5 mb-3"
                      style={{ boxShadow: `0 0 15px ${interest.color}40` }}
                    >
                      <interest.icon 
                        size={24} 
                        style={{ color: interest.color, filter: `drop-shadow(0 0 6px ${interest.color})` }} 
                      />
                    </div>
                    <p className="text-sm text-white font-medium">{interest.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EducationSection;